/**
 * Mouse look speed. Pure so it can be tested without a browser.
 *
 * Four independent speeds, each a multiple of the base turn per mouse pixel (1 = the original feel):
 *   hip:     horizontal = lookX,  vertical = lookY
 *   scoped:  horizontal = adsX,   vertical = adsY
 * Scoped speeds are also scaled by the sight's zoom (`zoom` = its field-of-view ratio, 1 = not aiming, 0.5 = half the field of view),
 * which keeps the picture moving at the same speed on screen at every zoom, so scoped 1.00 feels like hip 1.00 at any scope.
 */
export interface LookTuning {
  lookX: number;
  lookY: number;
  adsX: number;
  adsY: number;
}

export const DEFAULT_LOOK: LookTuning = { lookX: 1, lookY: 1, adsX: 1, adsY: 1 };

/** Slider range for every look speed. */
export const LOOK_MIN = 0.1;
export const LOOK_MAX = 4;

/** Radians of turn per pixel of mouse movement at speed 1 and no zoom. */
export const RADIANS_PER_PIXEL = 0.0022;

/** Horizontal and vertical radians per mouse pixel for the current zoom (`zoom < 1` means looking through a sight). */
export function lookSpeed(t: LookTuning, zoom: number): { x: number; y: number } {
  const scoped = zoom < 1;
  return {
    x: (scoped ? t.adsX : t.lookX) * RADIANS_PER_PIXEL * zoom,
    y: (scoped ? t.adsY : t.lookY) * RADIANS_PER_PIXEL * zoom,
  };
}

const num = (v: unknown): number | undefined => (typeof v === 'number' && Number.isFinite(v) ? v : undefined);
const clampLook = (v: number): number => Math.min(LOOK_MAX, Math.max(LOOK_MIN, v));

/**
 * Converts saved settings from the earlier model (one `sensitivity` plus `lookYScale`, `adsSensitivity`, `adsYScale` multipliers, and
 * a `fov`) to the four independent speeds, keeping every player's feel exactly (up to the slider range), and drops the old keys.
 * Settings that already have the new keys are left as they are.
 */
export function migrateLook(stored: Record<string, unknown>): Record<string, unknown> {
  const out = { ...stored };
  const hasNew = num(out.lookX) !== undefined;
  if (!hasNew) {
    const s = num(out.sensitivity);
    const y = num(out.lookYScale);
    const a = num(out.adsSensitivity);
    const ay = num(out.adsYScale);
    if (s !== undefined || y !== undefined || a !== undefined || ay !== undefined) {
      const base = s ?? 1;
      const adsX = base * (a ?? 1);
      out.lookX = clampLook(base);
      out.lookY = clampLook(base * (y ?? 1));
      out.adsX = clampLook(adsX);
      out.adsY = clampLook(adsX * (ay ?? 1));
    }
  }
  for (const k of ['sensitivity', 'lookYScale', 'adsSensitivity', 'adsYScale', 'fov']) delete out[k];
  return out;
}
